import { useState, useEffect } from "react";
import { XIcon } from "@heroicons/react/outline";

const AccommodationEditModal = ({ isOpen, onClose, initialData, onSave }) => {
  const [form, setForm] = useState({});

  useEffect(() => {
    if (initialData) {
      setForm({
        ...initialData,
        address: {
          address_1: initialData.address?.address_1 || "",
          address_2: initialData.address?.address_2 || "",
          address_3: initialData.address?.address_3 || "",
          city: initialData.address?.city || "",
          county: initialData.address?.county || "",
          country: initialData.address?.country || "",
          postcode: initialData.address?.postcode || "",
        },
      });
    }
  }, [initialData]);

  const handleChange = (key, value) => {
    setForm((prev) => ({ ...prev, [key]: value }));
  };

  const handleAddressChange = (key, value) => {
    setForm((prev) => ({
      ...prev,
      address: {
        ...prev.address,
        [key]: value,
      },
    }));
  };

  const StarRating = ({ value, max = 5, onChange }) => {
    return (
      <div className="flex items-center gap-1">
        {Array.from({ length: max }).map((_, i) => {
          const starValue = i + 1;
          const active = starValue <= value;

          return (
            <button
              key={starValue}
              type="button"
              onClick={() => onChange(starValue)}
              className={`text-xl transition ${
                active
                  ? "text-yellow-400"
                  : "text-slate-500 hover:text-yellow-300"
              }`}
            >
              ★
            </button>
          );
        })}

        {value > 0 && (
          <span className="ml-2 text-sm text-slate-300">
            {value} / {max}
          </span>
        )}
      </div>
    );
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60">
      <div className="w-full max-w-2xl bg-slate-900 border border-white/10 rounded-xl">
        {/* Header */}
        <div className="flex justify-between px-6 py-4 border-b border-white/10">
          <h2 className="text-white font-semibold">Edit Accommodation</h2>
          <button onClick={onClose}>
            <XIcon className="w-5 h-5 text-slate-400" />
          </button>
        </div>

        {/* Body */}
        <div className="px-6 py-5 space-y-4 max-h-[70vh] overflow-y-auto grid grid-cols-1 md:grid-cols-2 gap-4">
          <Input
            label="Hotel Name"
            value={form.hotel_name}
            onChange={(v) => handleChange("hotel_name", v)}
          />

          <div>
            <label className="block text-sm text-slate-300 mb-1">
              Hotel Rating
            </label>

            <StarRating
              value={Number(form.hotel_rating) || 0}
              max={7} // 👈 important (your system uses 7 stars)
              onChange={(val) => handleChange("hotel_rating", val)}
            />
          </div>

          <Input
            label="Trip Advisor Link"
            value={form.tripadvisor_link}
            onChange={(v) => handleChange("tripadvisor_link", v)}
          />
          <Input
            label="Facilities"
            value={form.facilities}
            onChange={(v) => handleChange("facilities", v)}
          />

          <Input
            label="Booking Name"
            value={form.contact_person}
            onChange={(v) => handleChange("contact_person", v)}
          />
          <Input
            label="Booking Number"
            value={form.booking}
            onChange={(v) => handleChange("booking", v)}
          />

          <Input
            label="Confirmation URL"
            value={form.confirmation_url}
            onChange={(v) => handleChange("confirmation_url", v)}
          />

          <Input
            label="Airport → Hotel Travel Time"
            value={form.airport_hotel_travel_time}
            onChange={(v) => handleChange("airport_hotel_travel_time", v)}
          />
          <Input
            label="Hotel → Venue Travel Time"
            value={form.hotel_venue_travel_time}
            onChange={(v) => handleChange("hotel_venue_travel_time", v)}
          />

          <Input
            label="Check-In"
            type="datetime-local"
            value={formatDateInput(form.check_in)}
            onChange={(v) => handleChange("check_in", v)}
          />
          <Input
            label="Check-Out"
            type="datetime-local"
            value={formatDateInput(form.check_out)}
            onChange={(v) => handleChange("check_out", v)}
          />

          {/* Address */}
          <div className="md:col-span-2">
            <h3 className="text-sm font-semibold text-white mb-3">Address</h3>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Input
                label="Address Line 1"
                value={form.address?.address_1}
                onChange={(v) => handleAddressChange("address_1", v)}
              />

              <Input
                label="Address Line 2"
                value={form.address?.address_2}
                onChange={(v) => handleAddressChange("address_2", v)}
              />

              <Input
                label="Address Line 3"
                value={form.address?.address_3}
                onChange={(v) => handleAddressChange("address_3", v)}
              />

              <Input
                label="City"
                value={form.address?.city}
                onChange={(v) => handleAddressChange("city", v)}
              />

              <Input
                label="County"
                value={form.address?.county}
                onChange={(v) => handleAddressChange("county", v)}
              />

              <Input
                label="Country"
                value={form.address?.country}
                onChange={(v) => handleAddressChange("country", v)}
              />

              <Input
                label="Postcode"
                value={form.address?.postcode}
                onChange={(v) => handleAddressChange("postcode", v)}
              />
            </div>
          </div>
        </div>

        {/* Footer */}
        <Footer onClose={onClose} onSave={() => onSave(form)} />
      </div>
    </div>
  );
};

// 🔥 Convert ISO → input format
const formatDateInput = (date) => {
  if (!date) return "";
  return new Date(date).toISOString().slice(0, 16);
};

const Input = ({ label, value, onChange, type = "text" }) => (
  <div>
    <label className="block text-sm text-slate-300 mb-1">{label}</label>
    <input
      type={type}
      value={value || ""}
      onChange={(e) => onChange(e.target.value)}
      className="w-full bg-[#131C31] border border-white/10 rounded-lg px-4 py-2.5 text-sm text-white
      focus:outline-none focus:ring-2 focus:ring-pink-500/40"
    />
  </div>
);

const Footer = ({ onClose, onSave }) => (
  <div className="flex justify-end gap-3 px-6 py-4 border-t border-white/10">
    <button
      onClick={onClose}
      className="px-4 py-2 text-sm rounded-lg text-slate-300 hover:bg-slate-800"
    >
      Cancel
    </button>

    <button
      onClick={onSave}
      className="px-4 py-2 text-sm rounded-lg text-white bg-pink-600 hover:bg-pink-700"
    >
      Save
    </button>
  </div>
);

export default AccommodationEditModal;
